// Credenciales: las claves de API, tokens y contraseñas de lo que el negocio conectó. Entran una vez y no vuelven a
// salir: acá se ven su nombre, sus últimos cuatro caracteres, quién las usa y cuándo se usaron por última vez.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Cargando, Input, Modal, PageHead, Tabla, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { desde } from '../lib/formato';
import { useSesion } from '../lib/sesion';
import { AvisoSoloVer, SoloSiAdministra } from '../componentes/Comunes';

type Edicion = { tipo: 'nueva' } | { tipo: 'rotar'; id: string; nombre: string } | null;

export function Credenciales() {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['secretos'], queryFn: () => api<any[]>('/api/secretos') });
  const [edicion, setEdicion] = useState<Edicion>(null);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [valor, setValor] = useState('');
  const [error, setError] = useState<string | null>(null);

  const abrir = (e: Edicion) => { setEdicion(e); setNombre(''); setDescripcion(''); setValor(''); setError(null); };
  const guardar = useMutation({
    mutationFn: () => (edicion?.tipo === 'rotar'
      ? api(`/api/secretos/${edicion.id}/rotar`, { valor })
      : api('/api/secretos', { nombre, descripcion: descripcion || null, valor })),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['secretos'] }); aviso(edicion?.tipo === 'rotar' ? 'Valor cambiado' : 'Credencial guardada y cifrada'); setEdicion(null); },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const borrar = useMutation({
    mutationFn: (id: string) => api(`/api/secretos/${id}/borrar`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['secretos'] }); aviso('Credencial borrada'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  if (q.isLoading) return <Cargando />;
  return (
    <div className="page">
      <PageHead title="Credenciales" subtitle="Claves de API, tokens y contraseñas del POS, la contabilidad y los proveedores de modelos. Se guardan cifradas y nunca se vuelven a mostrar: ni acá, ni a los agentes, ni al equipo de Glubi.">
        <SoloSiAdministra><Button icon="plus" onClick={() => abrir({ tipo: 'nueva' })}>Nueva credencial</Button></SoloSiAdministra>
      </PageHead>
      <AvisoSoloVer />
      {!q.data?.length ? (
        <Vacio titulo="Todavía no hay credenciales">También se pueden escribir al conectar un proveedor o un conector.</Vacio>
      ) : (
        <Tabla rowKey="id" rows={q.data}
          columns={[
            { key: 'nombre', label: 'Nombre', render: (s: any) => <div><b style={{ fontWeight: 500 }}>{s.nombre}</b>{s.descripcion && <div className="muted" style={{ fontSize: 12 }}>{s.descripcion}</div>}</div> },
            { key: 'pista', label: 'Valor', render: (s: any) => <code>{s.pista}</code> },
            { key: 'usos', label: 'La usan', render: (s: any) => [...s.proveedores, ...s.conectores].join(', ') || <span className="muted">Nadie</span> },
            { key: 'ultimo_uso_en', label: 'Último uso', render: (s: any) => desde(s.ultimo_uso_en) },
            { key: 'rotado_en', label: 'Cambiada', render: (s: any) => (s.rotado_en ? desde(s.rotado_en) : <span className="muted">Nunca</span>) },
            { key: 'acciones', label: '', render: (s: any) => (
              <SoloSiAdministra>
                <div className="fila acciones">
                  <Button size="sm" variant="secondary" icon="refresh" onClick={() => abrir({ tipo: 'rotar', id: s.id, nombre: s.nombre })}>Cambiar valor</Button>
                  <Button size="sm" variant="ghost" icon="trash" disabled={s.proveedores.length + s.conectores.length > 0}
                    onClick={() => { if (confirm(`¿Borrar «${s.nombre}»? No se puede deshacer.`)) borrar.mutate(s.id); }}>Borrar</Button>
                </div>
              </SoloSiAdministra>
            ) },
          ]} />
      )}

      {edicion && (
        <Modal title={edicion.tipo === 'rotar' ? `Cambiar el valor de «${edicion.nombre}»` : 'Nueva credencial'}
          subtitle={edicion.tipo === 'rotar' ? 'Todo lo que la usa toma el valor nuevo desde la próxima llamada.' : 'Se cifra antes de guardarse. Después solo se ven sus últimos cuatro caracteres.'}
          onClose={() => setEdicion(null)}
          footer={<>
            <Button variant="ghost" onClick={() => setEdicion(null)}>Cancelar</Button>
            <Button disabled={guardar.isPending || !valor || (edicion.tipo === 'nueva' && nombre.trim().length < 2)} onClick={() => guardar.mutate()}>Guardar</Button>
          </>}>
          {edicion.tipo === 'nueva' && <>
            <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Token del POS" autoFocus />
            <Input label="Para qué es (opcional)" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </>}
          <Input label="Valor" type="password" value={valor} onChange={(e) => setValor(e.target.value)} helper="Para autenticación básica: usuario:contraseña" />
          {error && <div className="error-caja">{error}</div>}
        </Modal>
      )}
    </div>
  );
}
